import { Immutable } from "immer";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { FormItem } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { CmsContainerBlock } from "@/gen/realtime";

import { FormControl, FormLabel } from "./shared";

const containerKinds = [
    { key: "tournament", kind: { t: "tournament", c: null } },
    { key: "other", kind: { t: "other", c: null } },
    { key: "no link", kind: { t: "noLink" } },
] as const;

export const ContainerForm = ({
    block,
    setBlock,
}: {
    block: Immutable<CmsContainerBlock>;
    setBlock: (mutate: (block: CmsContainerBlock) => void) => void;
}) => {
    return (
        <div className="flex-grow space-y-6">
            <Card>
                <CardHeader>
                    <CardTitle>Container Type</CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                    <div className="flex flex-row gap-4">
                        {containerKinds.map(kind => (
                            <Button
                                key={kind.key}
                                onClick={() => {
                                    setBlock(block => {
                                        block.kind = kind.kind;
                                    });
                                }}
                                variant={block.kind.t === kind.kind.t ? "default" : "outline"}
                            >
                                {kind.key}
                            </Button>
                        ))}
                    </div>
                    <ContainerKindForm block={block} setBlock={setBlock} />
                </CardContent>
            </Card>
            {block.kind.t !== "tournament" ? (
                <FormItem>
                    <FormLabel className="text-md font-bold">Title</FormLabel>
                    <FormControl>
                        <Input
                            placeholder="Teaser title"
                            value={block.title}
                            onChange={e =>
                                setBlock(block => {
                                    block.title = e.target.value;
                                })
                            }
                        />
                    </FormControl>
                </FormItem>
            ) : null}
            <FormItem>
                <FormLabel className="text-md font-bold">Subtitle</FormLabel>
                <FormControl>
                    <Input
                        placeholder="Lead text"
                        value={block.subtitle ?? ""}
                        onChange={e =>
                            setBlock(block => {
                                block.subtitle = e.target.value;
                            })
                        }
                    />
                </FormControl>
            </FormItem>
            <FormItem>
                <div className="flex items-center gap-2">
                    <Checkbox
                        id="transparent-checkbox"
                        checked={block.transparent}
                        onCheckedChange={newVal =>
                            setBlock(block => {
                                block.transparent = !!newVal;
                            })
                        }
                    />
                    <label htmlFor="transparent-checkbox" className="text-md font-bold">
                        Transparent
                    </label>
                </div>
                <div className="text-muted-foreground text-sm">
                    Transparent containers have no background, padding, or margin and don't increase
                    depth
                </div>
            </FormItem>
        </div>
    );
};

const ContainerKindForm = ({
    block,
    setBlock,
}: {
    block: Immutable<CmsContainerBlock>;
    setBlock: (mutate: (block: CmsContainerBlock) => void) => void;
}) => {
    switch (block.kind.t) {
        case "tournament":
            return (
                <div className="flex flex-col gap-2">
                    <div>
                        Tournament ID is used for link, part of the container header and for other
                        blocks that require a tournament ID.
                    </div>
                    <FormItem>
                        <FormLabel className="text-md font-bold">Tournament ID</FormLabel>
                        <FormControl>
                            <Input
                                placeholder="id"
                                value={block.kind.c ?? ""}
                                onChange={e =>
                                    setBlock(block => {
                                        block.kind = {
                                            t: "tournament",
                                            c: e.target.value,
                                        };
                                    })
                                }
                            />
                        </FormControl>
                    </FormItem>
                </div>
            );
        case "other":
            return (
                <FormItem>
                    <FormLabel className="text-md font-bold">URL (internal)</FormLabel>
                    <FormControl>
                        <Input
                            placeholder="/player/000-000"
                            value={block.kind.c ?? ""}
                            onChange={e =>
                                setBlock(block => {
                                    block.kind = {
                                        t: "other",
                                        c: e.target.value,
                                    };
                                })
                            }
                        />
                    </FormControl>
                </FormItem>
            );
        default:
            return null;
    }
};
